import { useCallback, useEffect, useRef, useState } from 'react';
import { useUnsavedChangesGuard } from 'components/OrganizationScope/context';
import type { SetStateAction } from 'react';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import {
  CARD_TYPE_ALT_TEXT_MAX_LENGTH,
  CARD_TYPE_MAX_CARDS,
  getCardTypeTemplate,
} from 'constants/cardTypeMessageTemplates';
import { lineStrings } from 'constants/strings';
import {
  ECardTypeTemplateId,
  TCardTypeCard,
  TCardTypeMessagePayload,
} from 'types/cardTypeMessage';
import { cn } from 'utils/cn';
import {
  buildCardTypeSavePayload,
  createDefaultCard,
} from 'utils/cardTypeMessage';
import { CardTypeMessageCardForm } from './CardTypeMessageCardForm';
import { CharCountInput } from './CardTypeMessageFormFields';
import { CardTypeMessagePicturesCarouselPreview } from './CardTypeMessagePicturesCarouselPreview';
import { CardTypeMessagePreview } from './CardTypeMessagePreview';
import { CardTypeMessageReorderModal } from './CardTypeMessageReorderModal';
import type { TCardTypeSortableCard } from './CardTypeMessageReorderModal';
import { CardTypeMessageTemplateModal } from './CardTypeMessageTemplateModal';
import {
  TCardTypeActionFieldErrors,
  TCardTypeCardFieldErrors,
  validateCardTypeMessageEditor,
} from './validateCardTypeMessageEditor';

const strings = lineStrings.management.cardTypeMessage;

export type CardTypeMessageEditorInitial = {
  itemId?: number | null;
  name?: string;
  altText?: string;
  templateId?: ECardTypeTemplateId;
  cards?: TCardTypeCard[];
};

type Props = {
  initial?: CardTypeMessageEditorInitial;
  isSaving: boolean;
  isDeleting?: boolean;
  isBusy?: boolean;
  canDelete?: boolean;
  onDelete?: () => void;
  onUploadError?: (message: string) => void;
  onUploadPendingChange?: (pending: boolean) => void;
  onSave: (payload: TCardTypeMessagePayload, name: string) => void;
  /** 保存が成功するたびに増える。今の入力を保存済みとして扱う合図。 */
  savedRevision?: number;
};

export function CardTypeMessageEditor({
  initial,
  isSaving,
  isDeleting = false,
  isBusy = false,
  canDelete,
  onDelete,
  onUploadError,
  onUploadPendingChange,
  onSave,
  savedRevision = 0,
}: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [altText, setAltText] = useState(initial?.altText ?? '');
  const [templateId, setTemplateId] = useState<ECardTypeTemplateId>(
    initial?.templateId ?? ECardTypeTemplateId.Products,
  );
  const nextEditorCardIdRef = useRef(0);
  const [editorCards, setEditorCards] = useState<TCardTypeSortableCard[]>(
    () => {
      const initialCards = initial?.cards?.length
        ? initial.cards
        : [createDefaultCard(ECardTypeTemplateId.Products)];
      return initialCards.map((card) => ({
        id: nextEditorCardIdRef.current++,
        card,
      }));
    },
  );
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [nameError, setNameError] = useState<string>();
  const [altTextError, setAltTextError] = useState<string>();
  const [cardError, setCardError] = useState<string>();
  const [actionErrors, setActionErrors] = useState<
    Record<number, TCardTypeActionFieldErrors>
  >({});
  const [cardFieldErrors, setCardFieldErrors] = useState<
    TCardTypeCardFieldErrors
  >({});

  const cards = editorCards.map((editorCard) => editorCard.card);
  const markSaved = useUnsavedChangesGuard(
    JSON.stringify({ name, altText, templateId, cards }),
    pendingUploadCount > 0,
  );
  useEffect(() => {
    if (savedRevision > 0) markSaved();
  }, [savedRevision, markSaved]);
  const selectedTemplate = getCardTypeTemplate(templateId);
  const activeEditorCard =
    editorCards[activeCardIndex] ?? editorCards[0]!;
  const activeCard = activeEditorCard.card;
  const isPicturesTemplate = templateId === ECardTypeTemplateId.Pictures;
  const canAddCard = cards.length < CARD_TYPE_MAX_CARDS;
  const canRemoveCard = cards.length > 1;
  const isUploading = pendingUploadCount > 0;
  const isEditorBusy = isBusy || isSaving || isDeleting || isUploading;

  const selectCard = (index: number) => {
    if (isEditorBusy) return;
    setActiveCardIndex(index);
    setActionErrors({});
    setCardFieldErrors({});
    setCardError(undefined);
  };

  const handleAddCard = () => {
    if (isEditorBusy || !canAddCard) return;
    const nextEditorCard = {
      id: nextEditorCardIdRef.current++,
      card: createDefaultCard(templateId),
    };
    setEditorCards((prev) =>
      prev.length < CARD_TYPE_MAX_CARDS
        ? [...prev, nextEditorCard]
        : prev,
    );
    setActiveCardIndex(Math.min(cards.length, CARD_TYPE_MAX_CARDS - 1));
    setActionErrors({});
    setCardFieldErrors({});
    setCardError(undefined);
  };

  const handleRemoveCard = () => {
    if (isEditorBusy || !canRemoveCard) return;
    const editorCardId = activeEditorCard.id;
    setEditorCards((prev) =>
      prev.filter((editorCard) => editorCard.id !== editorCardId),
    );
    setActiveCardIndex((prev) => Math.max(0, prev - 1));
    setActionErrors({});
    setCardFieldErrors({});
    setCardError(undefined);
  };

  const handleReorderCards = (nextCardIds: number[]): boolean => {
    if (
      isEditorBusy ||
      nextCardIds.length !== editorCards.length ||
      new Set(nextCardIds).size !== editorCards.length
    ) {
      return false;
    }
    const cardsById = new Map(editorCards.map((card) => [card.id, card]));
    const nextCards: TCardTypeSortableCard[] = [];
    for (const cardId of nextCardIds) {
      const card = cardsById.get(cardId);
      if (!card) return false;
      nextCards.push(card);
    }
    const activeEditorCardId = activeEditorCard.id;
    setEditorCards(nextCards);
    setActiveCardIndex(
      Math.max(
        0,
        nextCards.findIndex((card) => card.id === activeEditorCardId),
      ),
    );
    setActionErrors({});
    setCardFieldErrors({});
    setCardError(undefined);
    return true;
  };

  const handleTemplateChange = (nextId: ECardTypeTemplateId) => {
    if (isEditorBusy || nextId === templateId) return;
    setTemplateId(nextId);
    setEditorCards([
      {
        id: nextEditorCardIdRef.current++,
        card: createDefaultCard(nextId),
      },
    ]);
    setActiveCardIndex(0);
    setCardError(undefined);
    setActionErrors({});
    setCardFieldErrors({});
  };

  const updateActiveCard = (update: SetStateAction<TCardTypeCard>) => {
    const editorCardId = activeEditorCard.id;
    setEditorCards((prev) =>
      prev.map((editorCard) =>
        editorCard.id === editorCardId
          ? {
              ...editorCard,
              card:
                typeof update === 'function'
                  ? update(editorCard.card)
                  : update,
            }
          : editorCard,
      ),
    );
    setActionErrors({});
    setCardFieldErrors({});
  };

  const validate = useCallback((): boolean => {
    const validation = validateCardTypeMessageEditor({
      name,
      altText,
      templateId,
      cards,
    });

    setNameError(validation.nameError);
    setAltTextError(validation.altTextError);
    setCardError(validation.cardError);

    const errorCardIndex =
      validation.firstErrorCardIndex ?? activeCardIndex;
    if (validation.firstErrorCardIndex !== undefined) {
      setActiveCardIndex(validation.firstErrorCardIndex);
    }

    setCardFieldErrors(validation.cardFieldErrors[errorCardIndex] ?? {});

    const currentCardActionErrors: Record<number, TCardTypeActionFieldErrors> =
      {};
    Object.entries(validation.actionErrors).forEach(([key, value]) => {
      const [cardIndexRaw, actionIndexRaw] = key.split('-');
      if (Number.parseInt(cardIndexRaw, 10) === errorCardIndex) {
        currentCardActionErrors[Number.parseInt(actionIndexRaw, 10)] = value;
      }
    });
    setActionErrors(currentCardActionErrors);

    return validation.ok;
  }, [activeCardIndex, altText, cards, name, templateId]);

  const handleSave = () => {
    if (isEditorBusy) return;
    if (!validate()) return;
    const payload = buildCardTypeSavePayload(templateId, altText, cards);
    onSave(payload, name.trim());
  };

  return (
    <fieldset
      className="m-0 grid min-w-0 gap-6 border-0 p-0"
      disabled={isEditorBusy}
    >
      <div className="grid gap-4 rounded-2xl border border-border/60 bg-[#F9FAFB] p-4">
        <label className="grid gap-1 text-sm font-semibold text-ink">
          {strings.itemId}
          <Input
            value={initial?.itemId ? String(initial.itemId) : ''}
            disabled
            placeholder={strings.itemIdHint}
            className="bg-neutral-100 font-normal"
          />
        </label>
        <label className="grid gap-1 text-sm font-semibold text-ink">
          {strings.itemName}
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="font-normal"
          />
          <span className="text-right text-xs font-normal text-muted">
            {name.length}/100
          </span>
          <span className="text-xs font-normal text-muted">
            {strings.itemNameHint}
          </span>
          {nameError ? (
            <span className="text-xs font-normal text-red-600">
              {nameError}
            </span>
          ) : null}
        </label>
        <div>
          <CharCountInput
            label={strings.altText}
            value={altText}
            maxLength={CARD_TYPE_ALT_TEXT_MAX_LENGTH}
            onChange={setAltText}
          />
          {altTextError ? (
            <span className="text-xs font-normal text-red-600">
              {altTextError}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <span className="text-sm font-medium text-ink">
          {strings.cardTypeLabel}: {selectedTemplate?.label ?? '—'}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isEditorBusy}
          onClick={() => setTemplateModalOpen(true)}
        >
          {strings.changeType}
        </Button>
      </div>

      <div
        data-testid="card-type-editor-layout"
        className="grid grid-cols-1 gap-8 2xl:grid-cols-[320px_minmax(0,1fr)] 2xl:items-start"
      >
        <div className="flex min-w-0 shrink-0 justify-center 2xl:justify-start">
          {isPicturesTemplate ? (
            <CardTypeMessagePicturesCarouselPreview
              cards={cards}
              activeIndex={activeCardIndex}
              onSelectCard={selectCard}
            />
          ) : (
            <CardTypeMessagePreview templateId={templateId} card={activeCard} />
          )}
        </div>

        <div className="min-w-0 grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {editorCards.map((editorCard, index) => (
              <button
                key={editorCard.id}
                type="button"
                disabled={isEditorBusy}
                data-testid={`card-tab-${index + 1}`}
                onClick={() => selectCard(index)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                  index === activeCardIndex
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border bg-white text-ink hover:border-accent/60',
                )}
              >
                {strings.cardTab} {index + 1}
              </button>
            ))}
            {canAddCard ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isEditorBusy}
                onClick={handleAddCard}
              >
                {strings.addCard}
              </Button>
            ) : null}
            {canRemoveCard ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isEditorBusy}
                onClick={handleRemoveCard}
              >
                {strings.deleteCard}
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isEditorBusy || editorCards.length < 2}
              onClick={() => setReorderModalOpen(true)}
            >
              {strings.reorderCards}
            </Button>
          </div>

          <CardTypeMessageCardForm
            templateId={templateId}
            card={activeCard}
            onChange={updateActiveCard}
            onUploadPendingChange={(pending) => {
              setPendingUploadCount((count) =>
                Math.max(0, count + (pending ? 1 : -1)),
              );
              onUploadPendingChange?.(pending);
            }}
            onUploadError={onUploadError}
            actionErrors={actionErrors}
            cardFieldErrors={cardFieldErrors}
          />

          {cardError ? (
            <p className="text-xs text-red-600">{cardError}</p>
          ) : null}
        </div>
      </div>

      <div className="flex justify-between border-t border-border pt-4">
        <Button
          type="button"
          variant="outline"
          disabled={!canDelete || isEditorBusy}
          loading={isDeleting}
          onClick={() => {
            if (!isEditorBusy) onDelete?.();
          }}
        >
          {strings.delete}
        </Button>
        <Button
          type="button"
          disabled={isEditorBusy}
          loading={isSaving}
          onClick={handleSave}
        >
          {strings.save}
        </Button>
      </div>

      <CardTypeMessageTemplateModal
        open={templateModalOpen}
        selectedId={templateId}
        onClose={() => setTemplateModalOpen(false)}
        onApply={(id) => {
          handleTemplateChange(id);
          setTemplateModalOpen(false);
        }}
      />
      <CardTypeMessageReorderModal
        open={reorderModalOpen}
        templateId={templateId}
        cards={editorCards}
        disabled={isEditorBusy}
        onClose={() => setReorderModalOpen(false)}
        onApply={(nextCardIds) => {
          if (handleReorderCards(nextCardIds)) {
            setReorderModalOpen(false);
          }
        }}
      />
    </fieldset>
  );
}
